import { action, makeObservable, observable, runInAction } from 'mobx';
import { WorkflowMetadata } from '@bringg/types';
import { getSharedRootStore } from '@bringg-frontend/global-stores';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

/**
 * Store for workflow metadata (lightweight workflow data)
 * Used by dispatcher users (non-admins) to display workflow information in activity log and other features
 *
 * Admin users use the full workflow store loaded via workflowsProvider.loadAll()
 */
class DispatcherWorkflowMetadataStore {
	metadataMap: Map<number, WorkflowMetadata>;
	isLoaded: boolean;
	isLoading: boolean;
	error: Error | null;

	constructor() {
		this.metadataMap = new Map<number, WorkflowMetadata>();
		this.isLoaded = false;
		this.isLoading = false;
		this.error = null;

		makeObservable(this, {
			metadataMap: observable,
			isLoaded: observable,
			isLoading: observable,
			error: observable,
			load: action,
			clear: action
		});
	}

	/**
	 * Load workflow metadata for specific workflow IDs
	 * Automatically skips for admin users (they use the full workflow store)
	 * Only fetches workflows that aren't already cached
	 *
	 * @param workflowIds - Array of workflow IDs to fetch metadata for
	 */
	async load(workflowIds: number[]): Promise<void> {
		const { usersStore } = getSharedRootStore().data;
		if (usersStore.isAdmin) {
			return;
		}

		const uniqueIds = [...new Set(workflowIds)];
		const uncachedIds = uniqueIds.filter(id => !this.metadataMap.has(id));

		if (uncachedIds.length === 0) {
			return;
		}

		if (this.isLoading) {
			return;
		}

		this.isLoading = true;
		this.error = null;

		try {
			const { dashboardSdk } = getRootEnv();
			const response = await dashboardSdk.sdk.v2.workflows().getMetadata(uncachedIds);

			runInAction(() => {
				if (response.workflows && Array.isArray(response.workflows)) {
					response.workflows.forEach((wf: WorkflowMetadata) => {
						this.metadataMap.set(wf.id, wf);
					});
				}
				this.isLoaded = true;
				this.isLoading = false;
			});
		} catch (error) {
			runInAction(() => {
				this.error = error as Error;
				this.isLoading = false;
			});
		}
	}

	getTitle(workflowId: number): string | undefined {
		return this.metadataMap.get(workflowId)?.title;
	}

	clear(): void {
		this.metadataMap.clear();
		this.isLoaded = false;
		this.isLoading = false;
		this.error = null;
	}
}

export default DispatcherWorkflowMetadataStore;
